import React from 'react';

interface Props {
  fallback?: React.ReactNode;
  resetKeys?: unknown[];
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
}

interface State {
  error: Error | null;
}

const initialState: State = { error: null };

const changedArray = (prevArray: unknown[]=[], nextArray: unknown[]=[]) => {
  return (
    prevArray.length !== nextArray.length || 
    prevArray.some((item, index) => !Object.is(item, nextArray[index]))
  );
}

export default class ErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = initialState;
  }

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    this.props.onError?.(error, errorInfo);
  }

  componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>) {
    const { error } = this.state;
    const { resetKeys } = this.props;

    if (
      error !== null &&
      prevState.error !== null &&
      changedArray(prevProps.resetKeys, resetKeys)
    ) {
      this.reset();
    }
  }
  
  reset() {
    this.setState(initialState);
  }

  render() {
    const { error } = this.state;
    const { fallback, children } = this.props;

    if (error != null) {
      return fallback;
    }

    const hasOnlyChild = React.Children.count(children) === 1;

    return hasOnlyChild ? children : <div>{children}</div>;
  }
}